import React, {
  useEffect,
  useState,
} from 'react';
import {
  Box,
  IconButton,
  Typography,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Block';
import {
  StewartDialog,
  StewartDialogActions,
} from 'controls/global/dialogs/stewart-dialog/StewartDialog';
import ActionButton from 'controls/global/action-button';
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gradients,
  iconSize,
  padding,
  gaps,
} from 'theme/defaultStyle';

import TextInputField from 'controls/global/text-input-field';
import useFormWrapper from 'utils/custom-hooks/useFormWrapper';
import SelectField from 'controls/global/select-field';

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxWidth: "calc(40vw)",
    minWidth: "calc(40vw)",
    paddingTop: padding.xlarge21,
    paddingRight: padding.xlarge22,
    paddingBottom: padding.zero,
    paddingLeft: padding.xlarge22, 
    overflowX: "hidden",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.large1,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled(Box)({
  background: gradients.redGradient05,
  borderRadius: borderRadius.large,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled(Box)({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: "auto",
  width: "32px",
  height: "32px",
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
  },
  "& .iconButton": {
    padding: padding.zero,
  },
});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  padding: `${padding.large1} 0 ${padding.xlarge22}`,
});

type CancelTitleSearchDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  schema: string;
  productIndex: number;
  title?: string;
  buttonText?: string;
};

const CancelTitleSearchDialog = ({ isOpen, onClose, onSubmit, schema, title, buttonText, productIndex }: CancelTitleSearchDialogProps) => {
  const [productType, setProductType] = useState("");
  const [message, setMessage] = useState("");
  const { setValue, getValues } = useFormWrapper();
  const [currentProductType] = getValues([`${schema}.stewartProduct`]);

  useEffect(() => {
    if (isOpen) {
      setProductType("");
      setMessage("");
      setValue(`titleSearchProducts.${productIndex}.productActionMessageText`, "");
    }
  }, [isOpen]);
  const [messageError, setMessageError] = useState(false);
  
  const isSubmitDisabled =
  (productType.trim() === "" && message.trim() === "") || messageError ;

  return (
    <StyledDialog
      open={isOpen}
      onClose={onClose}
    >
      <StyledDialogTitle>
        <IconContainer>
          <CancelIcon />
        </IconContainer>
        <Typography>{
          title || "Cancel Title Search?"
          }</Typography>
        <CloseIconContainer>
          <IconButton
            className="iconButton"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </CloseIconContainer>
      </StyledDialogTitle>

      <Box
        mt={4}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <SelectField
          name={`${schema}.cancelMessageProductType`}
          defaultValue={currentProductType}
          disabled={true}
          label="Product Type"
          options={[
            {
              text: currentProductType,
              value: currentProductType,
            },
          ]}
        />
        <TextInputField
          name={`titleSearchProducts.${productIndex}.productActionMessageText`}
          multiline
          errorMaxLength={2500}
          errorMessageExceedLimit="Cannot exceed 2,500 characters"
          placeholder="Message"
          label="Message"
          onChange={(e: any) => setMessage(e.target.value)}
          onErrorChange={setMessageError}
        />
      </Box>

      <StyledDialogAction>
        <ActionButton
          variant="outlined"
          color="primary"
          onClick={() => onSubmit()}
          disabled={isSubmitDisabled}
        >
          {
            buttonText || "Cancel Title Search"
          }
        </ActionButton>
      </StyledDialogAction>
    </StyledDialog>
  );
};

export default CancelTitleSearchDialog;
